import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, ChevronDown, ChevronUp,
  Star, Eye, EyeOff, X, Upload, Camera,
} from 'lucide-react';
import { api } from '../api/client';
import { processImageForUpload } from '../utils/image';
import { getPersistedSizes } from '../utils/sizes';
import { InputPrice } from '../components/InputPrice';
import { useToast } from '../components/Toast';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Category { id: string; name: string; }
interface PendingImage { file: File; previewUrl: string; source: 'gallery' | 'camera'; }
interface UploadedImage { id: string; url: string; sortOrder: number; isPrimary: boolean; }
interface Product {
  id: string; sku: string; name: string; description: string;
  price: number; categoryId: string | null;
  gender: 'hombre' | 'mujer' | 'unisex';
  brand: string | null; material: string | null; color: string | null;
  badge: string | null; isActive: boolean; isFeatured: boolean;
  images: UploadedImage[];
  sizes: { id: string; size: string; stock: number }[];
}
interface Promotion {
  id: string; type: string; name: string;
  buyQty: number | null; payQty: number | null; discountValue: number | null;
  label: string; description: string | null;
  startsAt: number | null; endsAt: number | null; isActive: boolean;
}
interface FormDraft {
  name: string; badge: string; description: string; price: string;
  categoryId: string; gender: 'hombre' | 'mujer' | 'unisex';
  brand: string; material: string; color: string;
  isFeatured: boolean; isActive: boolean; sizes: string[];
  promoType: string; promoName: string; promoBuyQty: string; promoPayQty: string;
  promoDiscountValue: string; promoLabel: string; promoDescription: string;
  promoStartsAt: string; promoEndsAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const GENDER_OPTIONS = [
  { value: 'unisex', label: 'Unisex' },
  { value: 'hombre', label: 'Hombre' },
  { value: 'mujer',  label: 'Mujer'  },
] as const;

const inputCls    = 'w-full bg-white border border-page-border rounded-xl px-4 py-2.5 text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-colors text-sm';
const inputClsOpt = 'w-full bg-page-surface border border-page-border/60 rounded-xl px-4 py-2.5 text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/40 focus:ring-1 focus:ring-brand-glow transition-colors text-sm';
const inputErr    = 'w-full bg-white border border-rose-400 rounded-xl px-4 py-2.5 text-ink placeholder-ink-faint focus:outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200/50 transition-colors text-sm';

const fmt = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 0 });

const generateSku = (productName: string): string => {
  const parts = productName.trim().toUpperCase().replace(/[^A-Z0-9\s]/g, '').split(/\s+/).filter(Boolean).slice(0, 3);
  const prefix = parts.length > 0 ? parts.map(p => p.slice(0, 3)).join('-') : 'PRD';
  return `${prefix}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
};

const INITIAL_DRAFT: FormDraft = {
  name: '', badge: '', description: '', price: '',
  categoryId: '', gender: 'unisex',
  brand: '', material: '', color: '',
  isFeatured: false, isActive: true, sizes: [],
  promoType: 'none', promoName: '', promoBuyQty: '', promoPayQty: '',
  promoDiscountValue: '', promoLabel: '', promoDescription: '',
  promoStartsAt: '', promoEndsAt: '',
};

// Module-level draft — persists new-mode form across navigation to /categories
let _draftData: FormDraft = { ...INITIAL_DRAFT };
let _draftImages: PendingImage[] = [];

// ─── Sub-components ───────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-page-border shadow-sm p-5 lg:p-6 space-y-4">
    <h4 className="text-xs font-bold text-ink-faint uppercase tracking-widest pb-3 border-b border-page-border">{title}</h4>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; optional?: boolean }> = ({ label, children, hint, optional }) => (
  <div>
    <label className="flex items-center gap-2 text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
      {label}
      {optional && (
        <span className="text-[9px] font-semibold text-ink-faint bg-page-surface border border-page-border px-1.5 py-0.5 rounded normal-case tracking-normal">opcional</span>
      )}
    </label>
    {children}
    {hint && <p className="text-[10px] text-ink-faint mt-1">{hint}</p>}
  </div>
);

const Toggle: React.FC<{ label: string; icon: React.ReactNode; checked: boolean; onChange: (v: boolean) => void }> = ({ label, icon, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${checked ? 'bg-brand-surface border-brand-border text-brand-primary' : 'bg-page-surface border-page-border text-ink-muted hover:text-ink'}`}
  >
    {icon}{label}
    <span className={`w-7 h-4 rounded-full transition-colors relative ${checked ? 'bg-brand-primary' : 'bg-page-border'}`}>
      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${checked ? 'left-3.5' : 'left-0.5'}`} />
    </span>
  </button>
);

function PricePreview({ price, promoType, promoDiscountValue, promoLabel, badge }: {
  price: string; promoType: string; promoDiscountValue: string; promoLabel: string; badge: string;
}) {
  const base = Number(price);
  let finalPrice: number | null = null;
  let label = promoLabel || '';

  if (promoType === 'percentage' && Number(promoDiscountValue) > 0) {
    finalPrice = Math.round(base * (1 - Number(promoDiscountValue) / 100));
    if (!label) label = `-${promoDiscountValue}% OFF`;
  } else if (promoType === 'fixed' && Number(promoDiscountValue) > 0) {
    finalPrice = Math.max(0, base - Number(promoDiscountValue));
    if (!label) label = `-$${fmt(Number(promoDiscountValue))}`;
  }
  const hasQuantityPromo = ['bogo', 'nxm'].includes(promoType) && label;

  return (
    <div className="p-4 bg-page-surface rounded-xl border border-page-border">
      <span className="block text-[10px] font-bold text-ink-muted uppercase tracking-wider mb-2">Vista previa en tienda</span>
      <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-page-border/50 w-fit flex-wrap">
        <div className="flex flex-col">
          {finalPrice !== null ? (
            <>
              <span className="text-[11px] text-ink-faint line-through leading-none">${fmt(base)}</span>
              <span className="text-xl font-bold text-brand-primary leading-tight mt-0.5">${fmt(finalPrice)}</span>
            </>
          ) : (
            <span className="text-xl font-bold text-brand-primary leading-none">${fmt(base)}</span>
          )}
        </div>
        {finalPrice !== null && label && (
          <span className="bg-rose-50 text-rose-700 text-[10px] font-bold px-2 py-0.5 rounded border border-rose-200">{label}</span>
        )}
        {hasQuantityPromo && (
          <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-200">{label}</span>
        )}
        {badge && (
          <span className="bg-brand-surface border border-brand-border text-brand-primary text-[10px] font-bold px-2 py-0.5 rounded">{badge}</span>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [presetSizes] = useState<string[]>(() => getPersistedSizes());
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Edit-mode only
  const [product, setProduct] = useState<Product | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(isEdit);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [existingPromoId, setExistingPromoId] = useState<string | null>(null);

  // Form fields (restored from draft for new mode, empty for edit mode then filled from API)
  const d = _draftData;
  const [name, setName]               = useState(() => isEdit ? '' : d.name);
  const [badge, setBadge]             = useState(() => isEdit ? '' : d.badge);
  const [description, setDescription] = useState(() => isEdit ? '' : d.description);
  const [price, setPrice]             = useState(() => isEdit ? '' : d.price);
  const [categoryId, setCategoryId]   = useState(() => isEdit ? '' : d.categoryId);
  const [gender, setGender]           = useState<'hombre' | 'mujer' | 'unisex'>(() => isEdit ? 'unisex' : d.gender);
  const [brand, setBrand]             = useState(() => isEdit ? '' : d.brand);
  const [material, setMaterial]       = useState(() => isEdit ? '' : d.material);
  const [color, setColor]             = useState(() => isEdit ? '' : d.color);
  const [isFeatured, setIsFeatured]   = useState(() => isEdit ? false : d.isFeatured);
  const [isActive, setIsActive]       = useState(() => isEdit ? true : d.isActive);
  const [sizes, setSizes]             = useState<string[]>(() => isEdit ? [] : d.sizes);
  const [sku, setSku]                 = useState(''); // edit only

  const [promoType, setPromoType]                   = useState(() => isEdit ? 'none' : d.promoType);
  const [promoName, setPromoName]                   = useState(() => isEdit ? '' : d.promoName);
  const [promoBuyQty, setPromoBuyQty]               = useState(() => isEdit ? '' : d.promoBuyQty);
  const [promoPayQty, setPromoPayQty]               = useState(() => isEdit ? '' : d.promoPayQty);
  const [promoDiscountValue, setPromoDiscountValue] = useState(() => isEdit ? '' : d.promoDiscountValue);
  const [promoLabel, setPromoLabel]                 = useState(() => isEdit ? '' : d.promoLabel);
  const [promoDescription, setPromoDescription]     = useState(() => isEdit ? '' : d.promoDescription);
  const [promoStartsAt, setPromoStartsAt]           = useState(() => isEdit ? '' : d.promoStartsAt);
  const [promoEndsAt, setPromoEndsAt]               = useState(() => isEdit ? '' : d.promoEndsAt);

  // New mode: images queued until submit
  const [pendingImages, setPendingImages] = useState<PendingImage[]>(() => {
    if (isEdit) return [];
    const saved = _draftImages;
    _draftImages = [];
    return saved;
  });
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  const galleryRef     = useRef<HTMLInputElement>(null);
  const cameraRef      = useRef<HTMLInputElement>(null);
  const nameRef        = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const priceRef       = useRef<HTMLDivElement>(null);
  const explicitExitRef = useRef(false);

  // Keep refs fresh every render for unmount snapshot
  const stateRef = useRef({ name, badge, description, price, categoryId, gender, brand, material, color, isFeatured, isActive, sizes, promoType, promoName, promoBuyQty, promoPayQty, promoDiscountValue, promoLabel, promoDescription, promoStartsAt, promoEndsAt });
  const pendingRef = useRef(pendingImages);
  useEffect(() => {
    stateRef.current = { name, badge, description, price, categoryId, gender, brand, material, color, isFeatured, isActive, sizes, promoType, promoName, promoBuyQty, promoPayQty, promoDiscountValue, promoLabel, promoDescription, promoStartsAt, promoEndsAt };
  });
  useEffect(() => { pendingRef.current = pendingImages; });

  // ── Data loading ─────────────────────────────────────────────────────────────

  useEffect(() => {
    api.get<Category[]>('/api/v1/categories').then(r => setCategories(r.data)).catch(() => {});

    if (isEdit && id) {
      (async () => {
        try {
          const prodRes = await api.get<Product>(`/api/v1/products/${id}`);
          const p = prodRes.data;
          setProduct(p);
          setName(p.name); setSku(p.sku); setDescription(p.description);
          setBadge(p.badge ?? ''); setPrice(p.price.toString());
          setCategoryId(p.categoryId ?? ''); setGender(p.gender);
          setBrand(p.brand ?? ''); setMaterial(p.material ?? '');
          setColor(p.color ?? ''); setIsFeatured(p.isFeatured);
          setIsActive(p.isActive);
          setSizes(p.sizes.map(s => s.size));

          const promoRes = await api.get<Promotion[]>(`/api/v1/promotions?productId=${p.id}`);
          const active = promoRes.data.find(pr => pr.isActive);
          if (active) {
            setExistingPromoId(active.id);
            setPromoType(active.type);
            setPromoName(active.name);
            setPromoBuyQty(active.buyQty?.toString() ?? '');
            setPromoPayQty(active.payQty?.toString() ?? '');
            setPromoDiscountValue(active.discountValue?.toString() ?? '');
            setPromoLabel(active.label);
            setPromoDescription(active.description ?? '');
            setPromoStartsAt(active.startsAt ? new Date(active.startsAt * 1000).toISOString().slice(0, 16) : '');
            setPromoEndsAt(active.endsAt ? new Date(active.endsAt * 1000).toISOString().slice(0, 16) : '');
          }
        } catch {
          setErrors(['No se pudo cargar el producto.']);
        } finally {
          setLoadingProduct(false);
        }
      })();
    }

    // Save draft for new mode on unmount (unless cancelled/submitted)
    return () => {
      if (!isEdit && !explicitExitRef.current) {
        _draftData = { ...stateRef.current };
        _draftImages = pendingRef.current;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Image handlers ───────────────────────────────────────────────────────────

  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>, source: 'gallery' | 'camera') => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    if (isEdit) {
      if (!product) return;
      setUploadingImage(true);
      setImageError(null);
      (async () => {
        try {
          for (const file of files) {
            const webp = await processImageForUpload(file);
            const fd = new FormData();
            fd.append('file', webp);
            fd.append('source', source);
            await api.upload(`/api/v1/products/${product.id}/images`, fd);
          }
          const r = await api.get<Product>(`/api/v1/products/${product.id}`);
          setProduct(r.data);
        } catch { setImageError('Error al subir imagen'); }
        finally {
          setUploadingImage(false);
          if (galleryRef.current) galleryRef.current.value = '';
          if (cameraRef.current) cameraRef.current.value = '';
        }
      })();
    } else {
      setPendingImages(prev => [
        ...prev,
        ...files.map(file => ({ file, previewUrl: URL.createObjectURL(file), source })),
      ]);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (idx: number) => {
    if (isEdit) {
      if (!product) return;
      const sorted = product.images.slice().sort((a, b) => a.sortOrder - b.sortOrder);
      setImageError(null);
      try {
        await api.delete(`/api/v1/images/${sorted[idx].id}`);
        const r = await api.get<Product>(`/api/v1/products/${product.id}`);
        setProduct(r.data);
      } catch { setImageError('Error al eliminar imagen'); }
    } else {
      setPendingImages(prev => {
        URL.revokeObjectURL(prev[idx].previewUrl);
        return prev.filter((_, i) => i !== idx);
      });
    }
  };

  const handleMoveImage = async (idx: number, dir: -1 | 1) => {
    if (isEdit) {
      if (!product) return;
      const sorted = product.images.slice().sort((a, b) => a.sortOrder - b.sortOrder);
      const target = idx + dir;
      if (target < 0 || target >= sorted.length) return;
      const reordered = [...sorted];
      [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
      setImageError(null);
      try {
        await api.patch(`/api/v1/products/${product.id}/images/reorder`, {
          images: reordered.map((img, i) => ({ id: img.id, sortOrder: i })),
        });
        const r = await api.get<Product>(`/api/v1/products/${product.id}`);
        setProduct(r.data);
      } catch { setImageError('Error al reordenar'); }
    } else {
      setPendingImages(prev => {
        const next = [...prev];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return prev;
        [next[idx], next[target]] = [next[target], next[idx]];
        return next;
      });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const toggleSize = (sz: string) =>
    setSizes(prev => prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz]);

  const toUnixTs = (iso: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 1000);
  };

  const clearFieldError = (field: string) =>
    setFieldErrors(prev => { const n = new Set(prev); n.delete(field); return n; });

  const validate = () => {
    const errs: string[] = [];
    const fields = new Set<string>();
    if (name.trim().length < 3)        { errs.push('Nombre: mínimo 3 caracteres.'); fields.add('name'); }
    if (description.trim().length < 5) { errs.push('Descripción: mínimo 5 caracteres.'); fields.add('description'); }
    if (!price || Number(price) <= 0)  { errs.push('Precio debe ser mayor a 0.'); fields.add('price'); }
    return { errs, fields };
  };

  const handleCancel = () => {
    explicitExitRef.current = true;
    if (!isEdit) {
      _draftData = { ...INITIAL_DRAFT };
      pendingImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
      _draftImages = [];
    }
    navigate('/products');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { errs, fields } = validate();
    if (errs.length) {
      setErrors(errs);
      setFieldErrors(fields);
      const ref = fields.has('name') ? nameRef : fields.has('description') ? descriptionRef : priceRef;
      setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    setSaving(true);
    setErrors([]);
    setFieldErrors(new Set());

    const promoPayload = promoType !== 'none' ? {
      name: promoName.trim(), type: promoType,
      buyQty:        ['bogo', 'nxm'].includes(promoType) ? Number(promoBuyQty) : null,
      payQty:        ['bogo', 'nxm'].includes(promoType) ? Number(promoPayQty) : null,
      discountValue: promoType === 'percentage' ? Number(promoDiscountValue) : null,
      label: promoLabel.trim(),
      description: promoDescription.trim() || null,
      startsAt: toUnixTs(promoStartsAt), endsAt: toUnixTs(promoEndsAt),
      isActive: true,
    } : null;

    try {
      if (isEdit && product) {
        await api.put(`/api/v1/products/${product.id}`, {
          name: name.trim(), sku: sku.trim().toUpperCase(),
          description: description.trim(),
          price: Math.round(Number(price)),
          categoryId: categoryId || null, gender,
          brand: brand.trim() || null, material: material.trim() || null,
          color: color.trim() || null, badge: badge.trim() || null,
          isFeatured, isActive,
        });
        await api.put(`/api/v1/products/${product.id}/sizes`, {
          sizes: sizes.map(s => ({ size: s, stock: 0 })),
        });
        if (!promoPayload) {
          if (existingPromoId) await api.delete(`/api/v1/promotions/${existingPromoId}`);
        } else {
          if (existingPromoId) await api.put(`/api/v1/promotions/${existingPromoId}`, { productId: product.id, ...promoPayload });
          else                 await api.post('/api/v1/promotions', { productId: product.id, ...promoPayload });
        }
        toast.success({ title: 'Cambios guardados', description: `"${name.trim()}" actualizado.` });

      } else {
        const res = await api.post<{ id: string }>('/api/v1/products', {
          name: name.trim(), sku: generateSku(name),
          description: description.trim(),
          price: Math.round(Number(price)),
          categoryId: categoryId || null, gender,
          brand: brand.trim() || null, material: material.trim() || null,
          color: color.trim() || null, badge: badge.trim() || null,
          isFeatured, isActive,
          sizes: sizes.map(s => ({ size: s, stock: 0 })),
        });
        const productId = res.data.id;

        if (promoPayload) {
          await api.post('/api/v1/promotions', { productId, ...promoPayload });
        }

        const imgCount = pendingImages.length;
        let failedUploads = 0;
        for (let i = 0; i < pendingImages.length; i++) {
          setUploadProgress({ current: i + 1, total: imgCount });
          try {
            const webp = await processImageForUpload(pendingImages[i].file);
            const fd = new FormData();
            fd.append('file', webp);
            fd.append('source', pendingImages[i].source);
            await api.upload(`/api/v1/products/${productId}/images`, fd);
          } catch { failedUploads++; }
        }

        explicitExitRef.current = true;
        pendingImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
        _draftData = { ...INITIAL_DRAFT };

        if (failedUploads > 0) {
          toast.warning({ title: 'Producto creado con advertencias', description: `${failedUploads} imagen(es) no se pudieron subir.` });
        } else {
          toast.success({
            title: 'Producto creado',
            description: imgCount > 0
              ? `"${name.trim()}" creado con ${imgCount} imagen(es).`
              : `"${name.trim()}" fue agregado exitosamente.`,
          });
        }
      }
      navigate('/products');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setErrors([msg]);
      toast.error({
        title: isEdit ? 'No se pudo guardar el producto' : 'No se pudo crear el producto',
        description: 'Revisá tu conexión e intentá de nuevo. Si el problema persiste, contactá soporte.',
      });
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  // ── Image list (unified for both modes) ──────────────────────────────────────

  const imageItems = isEdit && product
    ? product.images.slice().sort((a, b) => a.sortOrder - b.sortOrder).map((img, i) => ({
        src: img.url,
        label: i === 0 ? 'Imagen principal' : `Imagen ${i + 1}`,
        source: '',
      }))
    : pendingImages.map((img, i) => ({
        src: img.previewUrl,
        label: i === 0 ? 'Imagen principal' : `Imagen ${i + 1}`,
        source: img.source,
      }));

  const btnLabel = uploadProgress
    ? `Subiendo imagen ${uploadProgress.current}/${uploadProgress.total}…`
    : saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear producto';

  // ── Render ───────────────────────────────────────────────────────────────────

  if (isEdit && loadingProduct) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (isEdit && !product) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-ink-muted text-sm">Producto no encontrado.</p>
        <button onClick={() => navigate('/products')} className="text-brand-primary text-sm hover:underline">← Volver</button>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-40 lg:pb-32">

      {/* Header */}
      <header className="flex items-center gap-4 border-b border-page-border pb-5">
        <button
          onClick={() => navigate('/products')}
          className="p-2 text-ink-muted hover:text-ink hover:bg-page-surface rounded-xl border border-page-border transition-colors flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <span className="text-xs font-semibold text-brand-primary uppercase tracking-widest">Productos</span>
          <h1 className="font-heading font-bold text-2xl lg:text-3xl text-ink mt-0.5">
            {isEdit ? 'Editar Producto' : 'Nuevo Producto'}
          </h1>
        </div>
      </header>

      <form id="product-form" onSubmit={handleSubmit} className="space-y-4 max-w-3xl">

        {errors.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-xl space-y-1 text-xs">
            {errors.map((e, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-rose-500 rounded-full flex-shrink-0" />
                <span>{e}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Información básica ── */}
        <Section title="Información básica">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2" ref={nameRef}>
              <Field label="Nombre del producto">
                <input
                  type="text" value={name}
                  onChange={e => { setName(e.target.value); clearFieldError('name'); }}
                  placeholder="Ej. Zapatillas Runner Air"
                  className={fieldErrors.has('name') ? inputErr : inputCls}
                  required
                />
                {fieldErrors.has('name') && <p className="text-[10px] text-rose-600 mt-1">Mínimo 3 caracteres</p>}
              </Field>
            </div>
            <Field label="Badge" optional>
              <input type="text" value={badge} onChange={e => setBadge(e.target.value)} placeholder="Ej. Nuevo, Oferta, Hot" className={inputClsOpt} />
            </Field>
            <div className="sm:col-span-2" ref={descriptionRef}>
              <Field label="Descripción">
                <textarea
                  value={description}
                  onChange={e => { setDescription(e.target.value); clearFieldError('description'); }}
                  rows={3}
                  placeholder="Descripción del producto para la tienda..."
                  className={`${fieldErrors.has('description') ? inputErr : inputCls} resize-none`}
                  required
                />
                {fieldErrors.has('description') && <p className="text-[10px] text-rose-600 mt-1">Mínimo 5 caracteres</p>}
              </Field>
            </div>
          </div>
        </Section>

        {/* ── Precio ── */}
        <Section title="Precio">
          <div ref={priceRef}>
            <Field label="Precio de venta ($ARS)">
              <InputPrice
                value={price}
                onChange={v => { setPrice(v); clearFieldError('price'); }}
                placeholder="Ej. 85.000" required
                className={fieldErrors.has('price') ? inputErr : inputCls}
              />
              {fieldErrors.has('price') && <p className="text-[10px] text-rose-600 mt-1">Ingresá un precio válido</p>}
            </Field>
          </div>
          {price && !isNaN(Number(price)) && Number(price) > 0 && (
            <PricePreview price={price} promoType={promoType} promoDiscountValue={promoDiscountValue} promoLabel={promoLabel} badge={badge} />
          )}
        </Section>

        {/* ── Promoción especial ── */}
        <Section title="Promoción especial (opcional)">
          <Field label="Tipo de promoción">
            <select
              value={promoType}
              onChange={e => {
                const v = e.target.value;
                setPromoType(v);
                if (v === 'bogo')            { setPromoLabel('2x1');   setPromoName(`2x1 en ${name || 'este producto'}`);       setPromoBuyQty('2'); setPromoPayQty('1'); }
                else if (v === 'nxm')        { setPromoLabel('3x2');   setPromoName(`3x2 en ${name || 'este producto'}`);       setPromoBuyQty('3'); setPromoPayQty('2'); }
                else if (v === 'percentage') { setPromoLabel('% OFF'); setPromoName(`Descuento en ${name || 'este producto'}`); setPromoBuyQty('');  setPromoPayQty('');  }
                else                         { setPromoLabel('');      setPromoName('');                                        setPromoBuyQty('');  setPromoPayQty('');  }
              }}
              className={inputCls}
            >
              <option value="none">Ninguna</option>
              <option value="bogo">2x1 — Llevás 2, pagás 1</option>
              <option value="nxm">NxM — ej: 3x2, 4x3</option>
              <option value="percentage">% OFF — Descuento porcentual</option>
            </select>
          </Field>

          {promoType !== 'none' && (
            <div className="p-4 bg-page-surface border border-page-border rounded-xl space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Field label="Nombre de la promo">
                    <input type="text" value={promoName} onChange={e => setPromoName(e.target.value)} placeholder="Ej. 3x2 en Zapatillas Urbanas" className={inputCls} required />
                  </Field>
                </div>
                {(promoType === 'bogo' || promoType === 'nxm') && (<>
                  <Field label="Llevás (cantidad)">
                    <input type="number" min="2" value={promoBuyQty}
                      onChange={e => { setPromoBuyQty(e.target.value); setPromoLabel(promoType === 'bogo' ? '2x1' : `${e.target.value}x${promoPayQty}`); }}
                      className={inputCls} required />
                  </Field>
                  <Field label="Pagás (cantidad)">
                    <input type="number" min="1" value={promoPayQty}
                      onChange={e => { setPromoPayQty(e.target.value); setPromoLabel(`${promoBuyQty}x${e.target.value}`); }}
                      className={inputCls} required />
                  </Field>
                </>)}
                {promoType === 'percentage' && (
                  <div className="col-span-2">
                    <Field label="Porcentaje de descuento (%)">
                      <input type="number" min="1" max="99" value={promoDiscountValue}
                        onChange={e => { setPromoDiscountValue(e.target.value); setPromoLabel(`${e.target.value}% OFF`); }}
                        className={inputCls} required />
                    </Field>
                  </div>
                )}
                <Field label="Etiqueta / Label">
                  <input type="text" value={promoLabel} onChange={e => setPromoLabel(e.target.value)} placeholder="Ej. 3x2, 20% OFF" className={inputCls} required />
                </Field>
                <div className="col-span-2">
                  <Field label="Descripción de la promo" optional>
                    <textarea value={promoDescription} onChange={e => setPromoDescription(e.target.value)} rows={2} className={`${inputClsOpt} resize-none`} />
                  </Field>
                </div>
                <Field label="Vigente desde" optional>
                  <input type="datetime-local" value={promoStartsAt} onChange={e => setPromoStartsAt(e.target.value)} className={inputClsOpt} />
                </Field>
                <Field label="Vigente hasta" optional>
                  <input type="datetime-local" value={promoEndsAt} onChange={e => setPromoEndsAt(e.target.value)} className={inputClsOpt} />
                </Field>
              </div>
            </div>
          )}
        </Section>

        {/* ── Clasificación ── */}
        <Section title="Clasificación">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoría" optional>
              <div className="relative">
                <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={`${inputClsOpt} appearance-none pr-9`}>
                  <option value="">Sin categoría</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint pointer-events-none" />
              </div>
              <button
                type="button"
                onClick={() => navigate('/categories')}
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-primary bg-brand-surface border border-brand-border px-2.5 py-1 rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
              >
                + Cargar nueva categoría
              </button>
            </Field>
            <Field label="Género">
              <div className="relative">
                <select value={gender} onChange={e => setGender(e.target.value as typeof gender)} className={`${inputCls} appearance-none pr-9`}>
                  {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint pointer-events-none" />
              </div>
            </Field>
            <Field label="Marca" optional>
              <input type="text" value={brand} onChange={e => setBrand(e.target.value)} placeholder="Ej. Nike" className={inputClsOpt} />
            </Field>
            <Field label="Material" optional>
              <input type="text" value={material} onChange={e => setMaterial(e.target.value)} placeholder="Ej. Cuero vacuno" className={inputClsOpt} />
            </Field>
            <Field label="Color" optional>
              <input type="text" value={color} onChange={e => setColor(e.target.value)} placeholder="Ej. Negro / Blanco" className={inputClsOpt} />
            </Field>
          </div>
        </Section>

        {/* ── Talles ── */}
        <Section title="Talles">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-ink-faint">Seleccioná los talles disponibles</span>
            <button
              type="button"
              onClick={() => navigate('/categories', { state: { tab: 'sizes' } })}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-primary bg-brand-surface border border-brand-border px-2.5 py-1 rounded-lg hover:bg-brand-primary hover:text-white transition-colors"
            >
              + Cargar nuevo talle
            </button>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-7 gap-2 bg-page-surface border border-page-border p-3 rounded-xl">
            {presetSizes.map(sz => (
              <button
                type="button" key={sz}
                onClick={() => toggleSize(sz)}
                className={`py-2 rounded-lg text-xs font-bold transition-all ${sizes.includes(sz) ? 'bg-brand-primary text-white' : 'bg-white border border-page-border text-ink-muted hover:text-ink hover:border-brand-border'}`}
              >
                {sz}
              </button>
            ))}
          </div>
          {sizes.length === 0 && <p className="text-xs text-ink-faint text-center py-2">Seleccioná al menos un talle</p>}
        </Section>

        {/* ── Imágenes ── */}
        <Section title="Imágenes">
          {imageItems.length > 0 && (
            <div className="space-y-2">
              {imageItems.map((img, i) => (
                <div key={i} className="flex items-center gap-3 bg-page-surface border border-page-border rounded-xl p-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center bg-white border border-page-border">
                    {i === 0
                      ? <span className="text-[9px] font-bold text-brand-primary leading-none">P</span>
                      : <span className="text-[11px] font-bold text-ink-muted leading-none">{i + 1}</span>
                    }
                  </div>
                  <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-page-border bg-white">
                    <img src={img.src} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-ink truncate">{img.label}</p>
                    {img.source && <p className="text-[10px] text-ink-faint capitalize">{img.source}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={() => handleMoveImage(i, -1)}
                      disabled={saving || uploadingImage || i === 0}
                      className="p-1.5 rounded-lg border border-page-border bg-white text-ink-muted hover:text-ink hover:border-brand-border transition-colors disabled:opacity-30 disabled:pointer-events-none">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => handleMoveImage(i, 1)}
                      disabled={saving || uploadingImage || i === imageItems.length - 1}
                      className="p-1.5 rounded-lg border border-page-border bg-white text-ink-muted hover:text-ink hover:border-brand-border transition-colors disabled:opacity-30 disabled:pointer-events-none">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => handleDeleteImage(i)}
                      disabled={saving || uploadingImage}
                      className="p-1.5 rounded-lg border border-page-border bg-white text-rose-500 hover:bg-rose-50 hover:border-rose-200 transition-colors disabled:pointer-events-none disabled:opacity-50">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isEdit && uploadingImage ? (
            <div className="flex items-center justify-center gap-2 h-16 bg-page-surface border border-page-border rounded-xl">
              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
              <span className="text-xs text-ink-muted">Procesando y subiendo...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className={`flex flex-col items-center justify-center gap-1.5 h-20 border-2 border-dashed rounded-xl bg-page-surface transition-colors group ${saving || uploadingImage ? 'opacity-50 cursor-not-allowed border-page-border' : 'cursor-pointer border-page-border hover:border-brand-primary/40'}`}>
                <Upload className="w-4 h-4 text-ink-faint group-hover:text-brand-primary transition-colors" />
                <span className="text-xs font-semibold text-ink-muted group-hover:text-brand-primary transition-colors">Galería</span>
                <span className="text-[9px] text-ink-faint">JPG, PNG, WEBP, HEIC</span>
                <input ref={galleryRef} type="file" accept="image/*,image/heic,image/heif" multiple
                  onChange={e => handleAddImages(e, 'gallery')} className="hidden" disabled={saving || uploadingImage} />
              </label>
              <label className={`flex flex-col items-center justify-center gap-1.5 h-20 border-2 border-dashed rounded-xl bg-page-surface transition-colors group ${saving || uploadingImage ? 'opacity-50 cursor-not-allowed border-page-border' : 'cursor-pointer border-page-border hover:border-brand-primary/40'}`}>
                <Camera className="w-4 h-4 text-ink-faint group-hover:text-brand-primary transition-colors" />
                <span className="text-xs font-semibold text-ink-muted group-hover:text-brand-primary transition-colors">Cámara</span>
                <span className="text-[9px] text-ink-faint">Foto directa</span>
                <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                  onChange={e => handleAddImages(e, 'camera')} className="hidden" disabled={saving || uploadingImage} />
              </label>
            </div>
          )}

          {imageError && <p className="text-xs text-rose-600">{imageError}</p>}
          {imageItems.length === 0 && !uploadingImage && (
            <p className="text-xs text-ink-faint text-center">La primera imagen será la principal</p>
          )}
        </Section>

      </form>

      {/* ── Fixed bottom bar ── */}
      <div className="fixed bottom-14 lg:bottom-0 left-0 lg:left-64 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-page-border shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
        <div className="flex flex-col px-4 lg:px-8 py-3 gap-2.5">
          <div className="flex items-center gap-2.5">
            <Toggle label="Destacado" icon={<Star className="w-3.5 h-3.5" />} checked={isFeatured} onChange={setIsFeatured} />
            <Toggle label="Activo" icon={isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} checked={isActive} onChange={setIsActive} />
            {!isEdit && pendingImages.length > 0 && (
              <span className="ml-auto text-[11px] text-ink-muted font-medium">
                {pendingImages.length} imagen{pendingImages.length !== 1 ? 'es' : ''} lista{pendingImages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button type="button" onClick={handleCancel} disabled={saving}
              className="flex-1 py-2.5 bg-page-surface text-ink-muted rounded-xl hover:bg-page-panel transition-colors text-sm font-medium border border-page-border disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" form="product-form" disabled={saving}
              className="flex-1 py-2.5 bg-brand-primary hover:bg-brand-hover disabled:opacity-50 text-white font-bold rounded-xl shadow-sm shadow-brand-glow transition-all text-sm flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />}
              {btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
